import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ResourceCard from '../components/resources/ResourceCard';
import SafeIcon from '../components/common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { SUGGESTED_TRAINING_TAGS } from '../constants/training';
import { fetchDistinctResourceCategories, fetchResources } from '../services/resources';

const audiences = ['Cualquiera', 'Cliente', 'Interno'];

const AUDIENCE_FILTER_MAP = {
  Cliente: 'cliente',
  Interno: 'interno',
};

export default function ResourceHubPage() {
  const navigate = useNavigate();
  const { canUploadResources } = useAuth();
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [activeAudience, setActiveAudience] = useState('Cualquiera');
  const [search, setSearch] = useState('');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(['Todos', ...SUGGESTED_TRAINING_TAGS]);

  useEffect(() => {
    let active = true;

    fetchDistinctResourceCategories()
      .then((fromDb) => {
        if (!active) return;
        const merged = new Set([...SUGGESTED_TRAINING_TAGS, ...fromDb]);
        setCategories(['Todos', ...[...merged].sort((a, b) => a.localeCompare(b, 'es'))]);
      })
      .catch((err) => console.error(err));

    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      try {
        const rows = await fetchResources({
          category: activeCategory === 'Todos' ? undefined : activeCategory,
          audience: AUDIENCE_FILTER_MAP[activeAudience],
          search: search.trim() || undefined,
        });
        if (active) setResources(rows);
      } catch (err) {
        console.error(err);
        if (active) setResources([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    const timer = setTimeout(load, search ? 250 : 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [activeCategory, activeAudience, search]);

  const filteredResources = useMemo(() => resources, [resources]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-24 transition-colors duration-300 relative">
      {canUploadResources && (
        <div className="absolute top-4 right-4 md:top-6 md:right-8 z-40">
          <button
            type="button"
            onClick={() => navigate('/app/recursos/subir')}
            className="flex items-center gap-2 gfi-gradient-bg text-brand-navy px-5 py-3 rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 transition-transform"
          >
            <SafeIcon name="Upload" className="w-4 h-4" />
            Subir recurso
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] pt-12 pb-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <h1 className="text-4xl md:text-6xl font-black text-[var(--text-main)] tracking-tighter uppercase italic leading-none mb-4">
                Resource <span className="gfi-gradient-text">Hub</span>
              </h1>
              <p className="text-[var(--text-secondary)] font-medium max-w-md uppercase text-xs tracking-[0.2em]">
                Herramientas listas para el campo de batalla. De líderes para líderes.
              </p>
            </div>

            {/* Search Bar */}
            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="BUSCAR HERRAMIENTA..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-full py-3 px-6 pr-12 text-xs font-bold text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green transition-all"
              />
              <SafeIcon name="Search" className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Area */}
      <div className="sticky top-0 z-30 bg-[var(--bg-base)]/80 backdrop-blur-md border-b border-[var(--border-subtle)] px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 items-center">

          {/* Audience Filter (Tabs) */}
          <div className="flex bg-[var(--bg-surface)] p-1 rounded-full border border-[var(--border-subtle)] shadow-sm">
            {audiences.map((aud) => (
              <button
                key={aud}
                type="button"
                onClick={() => setActiveAudience(aud)}
                className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeAudience === aud
                    ? 'gfi-gradient-bg text-brand-navy'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
                }`}
              >
                {aud}
              </button>
            ))}
          </div>

          <div className="h-4 w-[1px] bg-[var(--border-subtle)] hidden md:block" />

          {/* Category Filter (Pills) */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar w-full md:w-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`flex-none px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${
                  activeCategory === cat
                    ? 'bg-[var(--text-main)] text-[var(--bg-base)] border-[var(--text-main)]'
                    : 'bg-transparent border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--text-main)]/30'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
              Cargando recursos…
            </p>
          </div>
        ) : filteredResources.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredResources.map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <SafeIcon name="Folder" className="w-16 h-16 text-[var(--border-subtle)] mb-4" />
            <h3 className="text-xl font-black text-[var(--text-main)] uppercase italic">No encontramos ese recurso</h3>
            <p className="text-[var(--text-secondary)] text-sm mt-2">Prueba con otros filtros o términos de búsqueda.</p>
          </div>
        )}
      </div>
    </div>
  );
}
